import React, { useState, useEffect, useMemo, useRef } from "react";
import httpClient from "../../services/httpClient.js";
import Logger from "../../utils/Logger";
import { useAuth } from "../../AuthContext";
import { LoadingComponent } from "../../App.js";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import trashButtonIcon from "../../media/trash-button.svg";
import editButtonIcon from "../../media/edit-button.svg";
import agreeButtonIcon from "../../media/agree-button.svg";
import backButtonIcon from "../../media/back-button.svg";
import orangeSortIcon from "../../media/orange-sort-icon.svg";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../components/NotificationContext";
import eventBus from "../../components/eventBus";

const Schedule = () => {
  const { t, i18n } = useTranslation();
  const { userData } = useAuth();
  const { showNotification } = useNotification();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [hoveredPrevIcon, setHoveredPrevIcon] = useState(false);
  const [hoveredNextIcon, setHoveredNextIcon] = useState(false);
  const [isMonthLocked, setIsMonthLocked] = useState(false);
  const editingDayRef = useRef(null);

  const updateEditingDay = (patch) => {
    setEditingDay((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      editingDayRef.current = next;
      return next;
    });
  };

  useEffect(() => {
    editingDayRef.current = editingDay;
  }, [editingDay]);

  const localeCode = i18n.language === "en" ? "en-US" : "pl-PL";
  const monthNames = Array.from({ length: 12 }, (_, monthIndex) =>
    new Date(2026, monthIndex, 1).toLocaleDateString(localeCode, {
      month: "long",
    }),
  );

  const absenceTypes = [
    { value: "", label: t("schedule.regularDay") },
    { value: "university", label: t("schedule.absenceTypes.university") },
    { value: "holiday", label: t("schedule.absenceTypes.holiday") },
    { value: "personal", label: t("schedule.absenceTypes.personal") },
  ];

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const normalizeDateKey = (dateValue) => {
    if (typeof dateValue === "string") {
      return dateValue.slice(0, 10);
    }
    if (dateValue && typeof dateValue === "object") {
      const year = dateValue.year ?? dateValue.Year;
      const month = dateValue.month ?? dateValue.Month;
      const day = dateValue.day ?? dateValue.Day;
      if (year && month && day) {
        const pad = (value) => value.toString().padStart(2, "0");
        return `${year}-${pad(month)}-${pad(day)}`;
      }
    }
    return null;
  };

  const fetchScheduleData = async (signal = null, { silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const config = { withAuth: true };
      if (signal) {
        config.signal = signal;
      }
      const userIdParam = userData?.userId
        ? `&userId=${encodeURIComponent(userData.userId)}`
        : "";
      const response = await httpClient.get(
        `/Worklogs/report?year=${selectedYear}&month=${selectedMonth + 1}${userIdParam}`,
        config,
      );
      const entries = (response.data || []).filter((entry) => {
        if (!userData?.userId) return true;
        return entry?.userId === userData.userId;
      });
      setScheduleData(entries);
      Logger.log("Schedule data fetched:", response.data);
    } catch (error) {
      if (
        error.name === "AbortError" ||
        error.name === "CanceledError" ||
        error.code === "ERR_CANCELED" ||
        error.code === "ECONNABORTED"
      ) {
        Logger.log("Request was cancelled");
        return;
      }
      Logger.error("Error fetching schedule data:", error);
      setScheduleData([]);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const fetchLockStatus = async (signal = null) => {
    try {
      const config = { withAuth: true };
      if (signal) {
        config.signal = signal;
      }
      const response = await httpClient.get(
        `/Worklogs/lock?year=${selectedYear}&month=${selectedMonth + 1}`,
        config,
      );

      // API may return boolean or object { locked: boolean }
      const data = response.data;
      const locked = typeof data === "boolean" ? data : data?.locked || false;
      setIsMonthLocked(locked);
      Logger.log("Lock status fetched:", response.data);
    } catch (error) {
      if (
        error.name === "AbortError" ||
        error.name === "CanceledError" ||
        error.code === "ERR_CANCELED" ||
        error.code === "ECONNABORTED"
      ) {
        Logger.log("Request was cancelled");
        return;
      }
      Logger.error("Error fetching lock status:", error);
      // Jeśli błąd, zakładamy że miesiąc nie jest zablokowany
      setIsMonthLocked(false);
    }
  };

  useEffect(() => {
    const onLockChanged = (msg) => {
      try {
        // msg: { year: number, month: number, locked: boolean }
        if (!msg || typeof msg !== "object") return;
        const msgYear = Number(msg.year);
        const rawMonth = Number(msg.month);

        // normalize: accept both 0-based (0-11) and 1-based (1-12) months
        let msgMonth1Based = NaN;
        if (!Number.isNaN(rawMonth)) {
          if (rawMonth >= 0 && rawMonth <= 11) msgMonth1Based = rawMonth + 1;
          else msgMonth1Based = rawMonth; // assume already 1-based
        }

        if (msgYear === selectedYear && msgMonth1Based === selectedMonth + 1) {
          setIsMonthLocked(Boolean(msg.locked));
          // if month became locked, cancel any editing state
          if (msg.locked) {
            setEditingDay(null);
            setDeleteDialogOpen(false);
            setEntryToDelete(null);
          }
          // refresh schedule data to reflect any server-side changes
          fetchScheduleData();
        }
      } catch (e) {
        Logger.error("Error handling lock change message:", e);
      }
    };

    eventBus.on("worklogs-lock-changed", onLockChanged);
    return () => {
      eventBus.off("worklogs-lock-changed", onLockChanged);
    };
  }, [selectedMonth, selectedYear, userData?.userId]);

  useEffect(() => {
    const abortController = new AbortController();

    // reset lock state immediately when month/year changes
    setIsMonthLocked(false);

    const fetchData = async () => {
      await fetchScheduleData(abortController.signal);
      await fetchLockStatus(abortController.signal);
    };

    fetchData();

    return () => {
      abortController.abort();
    };
  }, [selectedMonth, selectedYear, userData?.userId]);

  const handleMonthChange = (direction) => {
    if (direction === "prev") {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const getEntryForDay = (day) => {
    const pad = (value) => value.toString().padStart(2, "0");
    const targetDate = `${selectedYear}-${pad(selectedMonth + 1)}-${pad(day)}`;

    return scheduleData.find((entry) => {
      const entryDate = normalizeDateKey(entry?.date);
      if (!entryDate || entryDate !== targetDate) return false;
      if (!userData?.userId) return true;
      return entry?.userId === userData.userId;
    });
  };

  const toTimeSpanHHmm = (hoursDecimal) => {
    const safeHours =
      Number.isFinite(hoursDecimal) && hoursDecimal > 0 ? hoursDecimal : 0;
    const totalMinutes = Math.round(safeHours * 60);
    const hh = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, "0");
    const mm = (totalMinutes % 60).toString().padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const mapAbsenceTypeToDayType = (absenceType) => {
    if (!absenceType) return "Regular";

    const mapping = {
      university: "University",
      holiday: "Holiday",
      personal: "Personal",
    };

    return mapping[absenceType] || "Regular";
  };

  const toHourInputValue = (hours) => {
    if (hours === null || hours === undefined || hours === "") {
      return "";
    }
    const parsed = Number(hours);
    return Number.isFinite(parsed) ? String(parsed) : "";
  };

  const toBaselineHours = (hours) => {
    const parsed = Number(hours);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Empty hour field keeps baseline from when edit mode was opened.
  const resolveHoursForSave = (inputValue, baselineHours) => {
    if (inputValue === "" || inputValue === null || inputValue === undefined) {
      return baselineHours;
    }
    const parsed = parseFloat(inputValue);
    return Number.isFinite(parsed) ? parsed : baselineHours;
  };

  const handleEditDay = (day) => {
    if (isMonthLocked) {
      return;
    }
    const entry = getEntryForDay(day);
    const next = {
      day,
      remoteHours: toHourInputValue(entry?.remoteHours),
      onsiteHours: toHourInputValue(entry?.onsiteHours),
      baselineRemoteHours: toBaselineHours(entry?.remoteHours),
      baselineOnsiteHours: toBaselineHours(entry?.onsiteHours),
      comment: entry?.comment || "",
      absenceType: entry?.absenceType || "",
      id: entry?.id || null,
    };
    editingDayRef.current = next;
    setEditingDay(next);
  };

  const handleSaveDay = async () => {
    const draft = editingDayRef.current;
    if (!draft) return;

    if (isMonthLocked) {
      return;
    }

    const remoteHours = resolveHoursForSave(
      draft.remoteHours,
      draft.baselineRemoteHours ?? 0,
    );
    const onsiteHours = resolveHoursForSave(
      draft.onsiteHours,
      draft.baselineOnsiteHours ?? 0,
    );
    const notes = (draft.comment ?? "").trim();

    if (remoteHours < 0 || onsiteHours < 0) {
      showNotification(t("schedule.saveError"), "error");
      return;
    }

    if (remoteHours > 24 || onsiteHours > 24) {
      showNotification(t("schedule.saveError"), "error");
      return;
    }

    const pad = (value) => value.toString().padStart(2, "0");
    const dateKey = `${selectedYear}-${pad(selectedMonth + 1)}-${pad(draft.day)}`;
    const dataToSave = {
      date: dateKey,
      remoteTime: toTimeSpanHHmm(remoteHours),
      officeTime: toTimeSpanHHmm(onsiteHours),
      dayType: mapAbsenceTypeToDayType(draft.absenceType),
      notes,
    };

    try {
      if (draft.id) {
        await httpClient.put(`/Worklogs/${draft.id}`, dataToSave, {
          withAuth: true,
        });
      } else {
        await httpClient.post("/Worklogs", dataToSave, { withAuth: true });

        // Ensure notes persist even if create mapping misses UserNotes.
        if (notes) {
          const response = await httpClient.get(
            `/Worklogs/report?year=${selectedYear}&month=${selectedMonth + 1}${
              userData?.userId
                ? `&userId=${encodeURIComponent(userData.userId)}`
                : ""
            }`,
            { withAuth: true },
          );
          const created = (response.data || []).find((entry) => {
            const sameDate = normalizeDateKey(entry?.date) === dateKey;
            if (!sameDate) return false;
            if (!userData?.userId) return true;
            return entry?.userId === userData.userId;
          });
          if (created?.id) {
            await httpClient.put(
              `/Worklogs/${created.id}`,
              { ...dataToSave, notes },
              { withAuth: true },
            );
          }
        }
      }
      setEditingDay(null);
      editingDayRef.current = null;
      await fetchScheduleData(null, { silent: true });
    } catch (error) {
      Logger.error("Error saving schedule entry:", error);
      showNotification(t("schedule.saveError"), "error");
    }
  };

  const handleDeleteDay = (day) => {
    if (isMonthLocked) {
      return;
    }
    const entry = getEntryForDay(day);
    if (entry) {
      setEntryToDelete(entry);
      setDeleteDialogOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (!entryToDelete) return;

    try {
      await httpClient.delete(`/Worklogs/${entryToDelete.id}`, {
        withAuth: true,
      });
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      await fetchScheduleData();
    } catch (error) {
      Logger.error("Error deleting schedule entry:", error);
      showNotification(t("schedule.deleteError"), "error");
    }
  };

  const totalHours = useMemo(() => {
    return scheduleData.reduce(
      (acc, entry) => {
        acc.remote += entry.remoteHours || 0;
        acc.onsite += entry.onsiteHours || 0;
        acc.total += (entry.remoteHours || 0) + (entry.onsiteHours || 0);
        return acc;
      },
      { remote: 0, onsite: 0, total: 0 },
    );
  }, [scheduleData]);

  const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <div className="schedule-container">
      <div className="schedule-header">
        <h1>{t("schedule.title")}</h1>
        <div className="month-selector">
          <div
            onClick={() => handleMonthChange("prev")}
            onMouseEnter={() => setHoveredPrevIcon(true)}
            onMouseLeave={() => setHoveredPrevIcon(false)}
            className="month-nav-icon"
            style={{
              backgroundImage: `url(${orangeSortIcon})`,
              transform: hoveredPrevIcon ? "rotate(180deg)" : "rotate(90deg)",
              cursor: "pointer",
            }}
          />
          <span className="month-display">
            {monthNames[selectedMonth]} {selectedYear}
          </span>
          <div
            onClick={() => handleMonthChange("next")}
            onMouseEnter={() => setHoveredNextIcon(true)}
            onMouseLeave={() => setHoveredNextIcon(false)}
            className="month-nav-icon"
            style={{
              backgroundImage: `url(${orangeSortIcon})`,
              transform: hoveredNextIcon ? "rotate(180deg)" : "rotate(270deg)",
              cursor: "pointer",
            }}
          />
        </div>
      </div>

      <div className="schedule-summary">
        <div className="summary-card">
          <h3>{t("schedule.monthSummary")}</h3>
          <div className="summary-row">
            <span>{t("schedule.remoteHours")}:</span>
            <strong>{totalHours.remote.toFixed(2)}h</strong>
          </div>
          <div className="summary-row">
            <span>{t("schedule.onsiteHours")}:</span>
            <strong>{totalHours.onsite.toFixed(2)}h</strong>
          </div>
          <div className="summary-row total">
            <span>{t("schedule.total")}:</span>
            <strong>{totalHours.total.toFixed(2)}h</strong>
          </div>
        </div>
      </div>

      {isMonthLocked && (
        <div className="locked-month-banner" style={{ marginTop: "12px" }}>
          <span style={{ color: "#000" }}>
            {t("schedule.monthLockedMessage")}
          </span>
        </div>
      )}

      <div className="schedule-table-container">
        <table className="schedule-table">
          <thead>
            <tr>
              <th>{t("schedule.day")}</th>
              <th>{t("common.date")}</th>
              <th>{t("schedule.absenceType")}</th>
              <th>{t("schedule.remoteHours")}</th>
              <th>{t("schedule.onsiteHours")}</th>
              <th>{t("schedule.dayTotal")}</th>
              <th>{t("schedule.comment")}</th>
              <th>{t("schedule.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day) => {
              const entry = getEntryForDay(day);
              const date = new Date(selectedYear, selectedMonth, day);
              const dayName = date.toLocaleDateString(localeCode, {
                weekday: "short",
              });
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const isEditing = editingDay?.day === day;
              const absenceTypeLabel =
                absenceTypes.find((type) => type.value === entry?.absenceType)
                  ?.label || t("schedule.regularDay");

              return (
                <tr
                  key={day}
                  className={`
                    ${isWeekend ? "weekend-row" : ""}
                    ${entry?.absenceType ? `absence-type-${entry.absenceType}` : ""}
                  `.trim()}
                >
                  <td>{dayName}</td>
                  <td>
                    {day}.{(selectedMonth + 1).toString().padStart(2, "0")}.
                    {selectedYear}
                  </td>
                  <td>
                    {isEditing ? (
                      <select
                        value={editingDay.absenceType}
                        onChange={(e) =>
                          updateEditingDay({ absenceType: e.target.value })
                        }
                        className="absence-type-select"
                      >
                        {absenceTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`absence-type-badge ${entry?.absenceType || ""}`}
                      >
                        {absenceTypeLabel}
                      </span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={editingDay.remoteHours}
                        onChange={(e) =>
                          updateEditingDay({ remoteHours: e.target.value })
                        }
                        className="hour-input"
                      />
                    ) : (
                      <span>
                        {Number.isFinite(Number(entry?.remoteHours))
                          ? Number(entry.remoteHours).toFixed(2)
                          : "0.00"}
                        h
                      </span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={editingDay.onsiteHours}
                        onChange={(e) =>
                          updateEditingDay({ onsiteHours: e.target.value })
                        }
                        className="hour-input"
                      />
                    ) : (
                      <span>
                        {Number.isFinite(Number(entry?.onsiteHours))
                          ? Number(entry.onsiteHours).toFixed(2)
                          : "0.00"}
                        h
                      </span>
                    )}
                  </td>
                  <td>
                    <strong>
                      {isEditing
                        ? (
                            resolveHoursForSave(
                              editingDay.remoteHours,
                              editingDay.baselineRemoteHours,
                            ) +
                            resolveHoursForSave(
                              editingDay.onsiteHours,
                              editingDay.baselineOnsiteHours,
                            )
                          ).toFixed(2)
                        : (
                            (entry?.remoteHours || 0) +
                            (entry?.onsiteHours || 0)
                          ).toFixed(2)}
                      h
                    </strong>
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingDay.comment}
                        onChange={(e) =>
                          updateEditingDay({ comment: e.target.value })
                        }
                        placeholder={t("schedule.addComment")}
                        className="comment-input"
                      />
                    ) : (
                      <div className="comment-cell comment-cell--stacked">
                        {entry?.comment ? (
                          <div className="student-comment">{entry.comment}</div>
                        ) : null}
                        {entry?.hrComment ? (
                          <div
                            className="hr-comment"
                            style={{ color: "#ff3333", fontStyle: "inherit" }}
                          >
                            {entry.hrComment}
                          </div>
                        ) : null}
                        {!entry?.comment && !entry?.hrComment ? "-" : null}
                      </div>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <div className="action-buttons">
                        <img
                          className="delete-btn"
                          src={trashButtonIcon}
                          alt={t("common.delete")}
                          style={{
                            cursor:
                              entry && !isMonthLocked
                                ? "pointer"
                                : "not-allowed",
                            width: "30px",
                            height: "30px",
                            opacity: entry && !isMonthLocked ? 1 : 0.3,
                          }}
                          onClick={
                            entry && !isMonthLocked
                              ? () => handleDeleteDay(day)
                              : null
                          }
                        />
                        <img
                          className="agree-btn"
                          src={agreeButtonIcon}
                          alt={t("common.save")}
                          style={{
                            cursor: isMonthLocked ? "not-allowed" : "pointer",
                            width: "30px",
                            height: "30px",
                            opacity: isMonthLocked ? 0.5 : 1,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isMonthLocked) {
                              handleSaveDay();
                            }
                          }}
                        />
                        <img
                          className="back-btn"
                          src={backButtonIcon}
                          alt={t("common.cancel")}
                          style={{
                            cursor: "pointer",
                            width: "40px",
                            height: "40px",
                          }}
                          onClick={() => setEditingDay(null)}
                        />
                      </div>
                    ) : (
                      <div className="action-buttons">
                        <img
                          className="delete-btn"
                          src={trashButtonIcon}
                          alt={t("common.delete")}
                          style={{
                            cursor:
                              entry && !isMonthLocked
                                ? "pointer"
                                : "not-allowed",
                            width: "30px",
                            height: "30px",
                            marginRight: "10px",
                            opacity: entry && !isMonthLocked ? 1 : 0.3,
                          }}
                          onClick={
                            entry && !isMonthLocked
                              ? () => handleDeleteDay(day)
                              : null
                          }
                        />
                        <img
                          className="edit-btn"
                          src={editButtonIcon}
                          alt={t("common.edit")}
                          style={{
                            cursor: isMonthLocked ? "not-allowed" : "pointer",
                            width: "36px",
                            height: "36px",
                            opacity: isMonthLocked ? 0.5 : 1,
                          }}
                          onClick={
                            isMonthLocked ? null : () => handleEditDay(day)
                          }
                        />
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setEntryToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={t("schedule.deleteEntry")}
        message={t("schedule.deleteEntryConfirm")}
      />
    </div>
  );
};

export default Schedule;
